import React from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { AddCircleOutlineOutlined, Delete, Edit } from "@mui/icons-material";
import { getRequest, deleteRequest, putRequest } from "Includes/api/apicall";
import { GET_URL, DEL_URL, PUT_URL } from "Includes/urls";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

function ExpenseTypeList(props) {
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [nameInput, setNameInput] = React.useState("");
  const [nameSearch, setNameSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const latestRequestRef = React.useRef(0);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingExpenseType, setEditingExpenseType] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState({
    name: '',
    description: ''
  });
  const [editErrors, setEditErrors] = React.useState({});
  const [saveLoading, setSaveLoading] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = nameInput.trim();
      setNameSearch((prev) => {
        if (prev !== trimmed) setPage(0);
        return trimmed;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [nameInput]);

  React.useEffect(() => {
    getExpenseTypesList(page, pageSize);
  }, [page, pageSize, nameSearch, statusFilter]);

  const getExpenseTypesList = (nextPage = page, nextPageSize = pageSize) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    const params = {
      limit: nextPageSize,
      pageno: nextPage + 1,
    };
    if (nameSearch) params.search = nameSearch;
    if (statusFilter === "all") params.include_inactive = 1;
    if (statusFilter === "inactive") params.include_inactive = 1;
    getRequest(GET_URL.expense_types.api, params, props)
      .then((response) => {
        if (latestRequestRef.current !== requestId) return;
        if (response && response.status === 200) {
          let data = response.data?.data || response.data || [];
          if (statusFilter === "inactive") data = data.filter((row) => !row.is_active);
          if (statusFilter === "active") data = data.filter((row) => row.is_active);
          setRows(data);
          setRowCount(response.data?.count || data.length);
        }
      })
      .finally(() => {
        if (latestRequestRef.current === requestId) setLoading(false);
      });
  };

  const handleEdit = (row) => {
    setEditingExpenseType(row);
    setEditFormData({
      name: row.name || '',
      description: row.description || '',
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const handleDelete = (row) => {
    deleteRequest(`${DEL_URL.expense_types.api}${row.id}/`, {}, props)
      .then((res) => {
        if (res?.status === 200) {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Deleted",
            text: "Expense Type deleted successfully",
            showConfirmButton: false,
            timer: 1500,
          });
          getExpenseTypesList();
        }
      });
  };

  const handleEditSave = () => {
    const errors = {};
    if (!editFormData.name.trim()) errors.name = "Name is required";

    if (Object.keys(errors).length) {
      setEditErrors(errors);
      return;
    }

    setSaveLoading(true);

    putRequest(
      `${PUT_URL.expense_types.api}${editingExpenseType.id}/`,
      {
        ...editFormData,
        code: editingExpenseType.code,
      },
      props
    ).then((res) => {
      setSaveLoading(false);
      if (res?.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Updated",
          text: "Expense Type updated successfully",
          showConfirmButton: false,
          timer: 1500,
        });
        setEditDialogOpen(false);
        getExpenseTypesList();
      }
    });
  };

  const columns = [
    { field: "code", headerName: "Code", minWidth: 140, flex: 0.7 },
    { field: "name", headerName: "Name", minWidth: 200, flex: 1 },
    { field: "description", headerName: "Description", minWidth: 240, flex: 1.2 },
    {
      field: "is_active",
      headerName: "Status",
      minWidth: 120,
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => handleEdit(params.row)} />,
        <GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => handleDelete(params.row)} showInMenu />,
      ],
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Expense Type Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all your expense types in one place
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/expenses/expense-type/add"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{ borderRadius: "10px", textTransform: "none", px: 2, py: 1, boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }}
          >
            Add Expense Type
          </Button>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by name"
              placeholder="Type to filter..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: "16px", boxShadow: "0 6px 20px rgba(0,0,0,0.05)" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Paper>

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Expense Type</DialogTitle>

        <DialogContent>
          <Box mt={2}>

            {/* Code Box */}
            <Box mb={3} p={2} sx={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 2 }}>
              <Typography variant="caption">Code</Typography>
              <Typography fontWeight={700}>
                {editingExpenseType?.code || "-"}
              </Typography>
            </Box>

            <TextField
              label="Name"
              fullWidth
              required
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              error={!!editErrors.name}
              helperText={editErrors.name}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ExpenseTypeList;